import type { SVGProps } from 'react';
import { ICONS, type IconDef, type IconName, type Paint } from './iconData';

export type { IconName } from './iconData';
export { ICON_NAMES } from './iconData';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'fill' | 'stroke'> {
  name: IconName;
  /** Pixels (width and height). Default 20. */
  size?: number;
  strokeWidth?: number;
  /** Fill closed shapes with the icon colour instead of leaving them hollow. */
  filled?: boolean;
  /**
   * Gives the icon a name for screen readers. Without it the icon is
   * decorative (`aria-hidden`): the button or text next to it carries the meaning.
   */
  label?: string;
}

const DEFAULT_VIEW_BOX = '0 0 24 24';

function paint(value: Paint | undefined, fallback: string): string {
  if (value === undefined) return fallback;
  return value === 'color' ? 'currentColor' : value;
}

/**
 * Kindra's icons as inline SVG (ADR-W01). The colour is `currentColor`, so an
 * icon takes the text colour of wherever it sits and follows dark mode.
 */
export function Icon({
  name,
  size = 20,
  strokeWidth = 2,
  filled = false,
  label,
  ...rest
}: IconProps) {
  const def: IconDef = ICONS[name];
  const viewBox = def.viewBox ?? DEFAULT_VIEW_BOX;
  const [, , width = 24, height = 24] = viewBox.split(' ').map(Number);

  const shapes = def.shapes.map((shape, index) => {
    const common = {
      fill: paint(shape.fill, filled ? 'currentColor' : 'none'),
      stroke: paint(shape.stroke, 'currentColor'),
      strokeWidth: shape.sw ?? strokeWidth,
      strokeLinecap: 'round' as const,
      strokeLinejoin: 'round' as const,
    };
    switch (shape.k) {
      case 'path':
        return <path key={index} {...common} d={shape.d} />;
      case 'circle':
        return <circle key={index} {...common} cx={shape.cx} cy={shape.cy} r={shape.r} />;
      case 'rect':
        return (
          <rect
            key={index}
            {...common}
            x={shape.x}
            y={shape.y}
            width={shape.w}
            height={shape.h}
            rx={shape.rx}
          />
        );
      case 'polyline':
        return <polyline key={index} {...common} points={shape.points} />;
    }
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      focusable="false"
      data-icon={name}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      {...rest}
    >
      {def.rotate ? (
        <g transform={`rotate(${def.rotate} ${width / 2} ${height / 2})`}>{shapes}</g>
      ) : (
        shapes
      )}
    </svg>
  );
}
